import type { DriverFile } from "./ports";

export type SourceReadStrategy = "slice" | "arrayBuffer" | "stream";

/**
 * A durable session must keep using the strategy selected when its source was adopted. If a
 * replacement source cannot provide that strategy, retrying with a different reader can lose
 * bytes or force a whole-file read. Callers should persist a needs-attention outcome instead.
 */
export class PersistedSourceReadStrategyUnavailableError extends Error {
	readonly strategy: SourceReadStrategy;
	readonly fileName: string;

	constructor(strategy: SourceReadStrategy, fileName: string) {
		super(`Persisted upload source strategy "${strategy}" is unavailable for "${fileName}".`);
		this.name = "PersistedSourceReadStrategyUnavailableError";
		this.strategy = strategy;
		this.fileName = fileName;
	}
}

/**
 * The shared port intentionally keeps stream optional while consumers migrate. A web File with a
 * stream is the only source this slice selects for the sequential path; native adapters can opt in
 * later without being forced to stage bytes in this package.
 */
export type StreamCapableDriverFile = DriverFile & {
	stream?: () => ReadableStream<Uint8Array>;
};

/** Existing cloud-file memory cap. The foundation integration may override it for testing. */
export const DEFAULT_MAX_ARRAY_BUFFER_BYTES = 500 * 1024 * 1024;
export const DEFAULT_LARGE_FILE_STREAM_THRESHOLD_BYTES = DEFAULT_MAX_ARRAY_BUFFER_BYTES;

export interface SourceReadStrategyOptions {
	maxArrayBufferBytes?: number;
	largeFileThresholdBytes?: number;
}

/**
 * Chooses how the source bytes are obtained. The order is deliberate:
 * - preserve the existing one-read cloud path for small cloud-hosted files;
 * - use one forward-only pass for large web Files when available;
 * - otherwise retain the existing slice path until the consumer can provide a stream.
 *
 * A stream read failure is terminal for this selection. Callers must not downgrade a selected
 * stream source to random slicing because that is the failure mode this path removes.
 */
export function selectSourceReadStrategy(
	file: Pick<StreamCapableDriverFile, "size" | "stream">,
	cloudHosted: boolean,
	options: SourceReadStrategyOptions = {}
): SourceReadStrategy {
	const maxArrayBufferBytes = options.maxArrayBufferBytes ?? DEFAULT_MAX_ARRAY_BUFFER_BYTES;
	const largeFileThresholdBytes = options.largeFileThresholdBytes ?? DEFAULT_LARGE_FILE_STREAM_THRESHOLD_BYTES;
	if (cloudHosted && file.size <= maxArrayBufferBytes) return "arrayBuffer";
	if (file.size > largeFileThresholdBytes && typeof file.stream === "function") return "stream";
	return "slice";
}
